import { Delete, Edit } from '@mui/icons-material';
import { Chip, Grid, Paper } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { AccessPermissionInterface } from '@thro-types/access-handler';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAccessPermissionsMutation, useAccessPermissionsQuery } from 'api/hooks/accessHandler';
import { CreateButton, TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { PermissionModal } from './PermissionModal';

export function AccessPermissions() {
  const [currentlyEditingPermission, setCurrentlyEditingPermission] = useState<
    AccessPermissionInterface | undefined
  >(undefined);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  usePageHeading({
    heading: t(translations.pages.managePermissionsSettings.title()),
    headingProps: { back: location.state?.back ?? `/settings/access-handler` }
  });

  const {
    data: accessPermissions,
    isLoading,
    isError
  } = useAccessPermissionsQuery({
    params: {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }
  });

  const { deletion } = useAccessPermissionsMutation();

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleDelete = async () => {
    try {
      if (!currentlyEditingPermission) {
        return;
      }
      await deletion.mutateAsync(currentlyEditingPermission.id);
      dispatch(AlertActions.success(t(translations.pages.permissions.alerts.deleteSuccess())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.permissions.alerts.deleteFailed())));
    } finally {
      setCurrentlyEditingPermission(undefined);
    }
  };

  const cancelPermissionCreation = (_event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setCurrentlyEditingPermission(undefined);
    setDisabledCancel(true);
    setFormModalOpen(false);
    setDisabledCancel(false);
  };

  const rowActions = useCallback(
    (params: GridRowParams<AccessPermissionInterface>) => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setCurrentlyEditingPermission(params.row);
            setFormModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Delete
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => {
            setCurrentlyEditingPermission(params.row);
            setDeleteModalOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<AccessPermissionInterface> => {
    return [
      {
        field: 'id',
        flex: 1,
        headerName: t(translations.common.id()),
        align: 'center',
        headerAlign: 'center'
      },
      {
        field: 'permission',
        flex: 10,
        headerName: t(translations.pages.roles.model.permissions()),
        renderCell: params => {
          return (
            <Grid container style={{ height: 'fit-content' }}>
              <Chip
                key={params.row.id}
                label={params.row.permission}
                style={{ margin: '0.25em' }}
              />
            </Grid>
          );
        }
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  return (
    <Grid container>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.managePermissionsSettings.deletePermission())}
        onClose={() => {
          setDeleteModalOpen(false);
          setCurrentlyEditingPermission(undefined);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          setCurrentlyEditingPermission(undefined);
          handleDelete();
        }}
      >
        {t(translations.pages.permissions.deleteConfirm.message())}
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="roleModal">
        <Modal
          open={formModalOpen}
          onClose={cancelPermissionCreation}
          headline={'Role'}
          onCancel={cancelPermissionCreation}
          disabledCancel={disabledCancel}
        >
          <PermissionModal
            closeHandler={cancelPermissionCreation}
            setDisabledCancel={setDisabledCancel}
            onCancel={() => {
              setFormModalOpen(false);
              setCurrentlyEditingPermission(undefined);
            }}
            permission={currentlyEditingPermission}
          />
        </Modal>
      </ErrorBoundary>
      <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} marginBottom={'1em'}>
        <CreateButton onClick={() => setFormModalOpen(true)} />
      </Grid>
      <Grid container component={Paper} style={{ height: '70vh' }}>
        <DataGrid
          getEstimatedRowHeight={() => 100}
          getRowHeight={() => 'auto'}
          style={{ height: '100%', width: '100%' }}
          disableColumnFilter
          disableRowSelectionOnClick
          disableColumnMenu
          columns={columns}
          rows={accessPermissions?.data || []}
          rowCount={accessPermissions?.pagination.count || 0}
          paginationMode="server"
          paginationModel={{
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
          }}
          onPaginationModelChange={pagModel => {
            handlePaginationChange(pagModel);
          }}
          loading={isLoading}
        />
      </Grid>
    </Grid>
  );
}
